'use client';

import { useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from '@/components';
import ROUTES from '@/router/routes';
import type { SelectedScreen } from '@/types/editProfile';

type TypeModalState = {
  visibility: boolean;
  title: string;
  subtitle?: string;
  returnPage?: keyof typeof ROUTES;
};

type ModalConfig = {
  id: number;
  title: string;
  subtitle?: string;
  returnPage?: keyof typeof ROUTES;
};

export const useMultiModalWinner = () => {
  const navigate = useNavigate();
  const [modals, setModals] = useState<Record<string, TypeModalState>>({});
  const modalRefs = useRef<Record<string, boolean>>({});

  const openModal = useCallback((config: ModalConfig) => {
    const { id, ...modalData } = config;

    setModals((prev) => ({
      ...prev,
      [id]: {
        visibility: true,
        ...modalData,
      },
    }));

    modalRefs.current[id] = true;
  }, []);

  const closeModal = useCallback((id: number) => {
    setModals((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        visibility: false,
      },
    }));

    modalRefs.current[id] = false;
  }, []);

  const closeAllModals = useCallback(() => {
    setModals((prev) => {
      const updatedModals = { ...prev };
      Object.keys(updatedModals).forEach((id) => {
        updatedModals[id] = {
          ...updatedModals[id],
          visibility: false,
        };
        modalRefs.current[id] = false;
      });
      return updatedModals;
    });
  }, []);

  const isModalOpen = useCallback((id: number) => {
    return modalRefs.current[id] || false;
  }, []);

  const ContentModal = useCallback(
    ({ modalId }: { modalId: number }) => {
      const modal = modals[modalId];
      if (!modal) return null;

      return (
        <div className="flex flex-col items-center content-center mt-4">
          <p className="text-center font-semibold mt-2">
            Necesitamos que nos compartas{' '}
            <span
              className="text-blue-500 underline cursor-pointer"
              onClick={() => {
                navigate(ROUTES.editAccount.myCBU, {
                  state: {
                    ...(modal.returnPage && {
                      returnPage: modal.returnPage,
                    }),
                  } as SelectedScreen,
                });
              }}
            >
              acá
            </span>{' '}
            tus datos para que puedas recibir tu premio por transferencia
            bancaria.
          </p>
        </div>
      );
    },
    [modals, navigate]
  );

  const ModalWinner = useCallback(
    ({ modalId }: { modalId: number }) => {
      const modal = modals[modalId];
      if (!modal) return null;

      return (
        <Modal
          controlledOnClose={() => closeModal(modalId)}
          disableBodyScroll={true}
          isControlledOpen={modal.visibility}
          render={() => <ContentModal modalId={modalId} />}
          size="xs"
          title={modal.title || ''}
          variant="controlled"
        />
      );
    },
    [modals, closeModal, ContentModal]
  );

  return {
    ModalWinner,
    openModal,
    closeModal,
    closeAllModals,
    isModalOpen,
    modals,
  };
};
